import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Clock, Users, Activity, Mic, TrendingUp, MessageSquare, Calendar, BarChart3, AlertTriangle, RefreshCw } from 'lucide-react';
import type { SpeakingMetricsDoc, TutorAnalyticsSummary } from '../../tutor/types/tutorAnalytics';
import { resolveSpeakingTopicLabel } from '../../tutor/types/tutorAnalytics';
import { TutorKpiCards } from '../ai-tutor/analytics/TutorKpiCards';
import { TutorTrendChart } from '../ai-tutor/analytics/TutorTrendChart';

interface Props {
  data: TutorAnalyticsSummary | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  days?: number;
}

export const ParentTutorAnalyticsPanel: React.FC<Props> = ({ data, loading, error, onRetry, days = 30 }) => {
  const showApiWarning = !!error && (error.includes('Server error') || error.includes('Cannot connect'));

  const formatDuration = (minutes: number) => {
    const value = Math.round(minutes * 10) / 10;
    return `${value}m`;
  };

  if (loading) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          AI Tutor Analytics (Last {days} Days)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          AI Tutor Analytics (Last {days} Days)
        </h2>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4">
              <p className="text-gray-600 dark:text-gray-400">
                {error || 'No analytics data available'}
              </p>
              {onRetry && (
                <div className="mt-3">
                  <button onClick={onRetry} className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1">
                    <RefreshCw className="w-4 h-4" />
                    Retry
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          AI Tutor Analytics (Last {days} Days)
        </h2>
        {showApiWarning && onRetry && (
          <button
            onClick={onRetry}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        )}
      </div>

      {showApiWarning && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">
                Analytics service temporarily unavailable. Showing cached data where available.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <TutorKpiCards data={data} />

      {/* Session History Summary */}
      {data.session_history && data.session_history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Session History ({data.session_history.length} sessions)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {data.session_history.filter(s => s.mode === 'VOICE').length}
                </div>
                <div className="text-gray-500">Voice Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {data.session_history.filter(s => s.mode === 'TEXT').length}
                </div>
                <div className="text-gray-500">Text Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {Math.round(data.session_history.reduce((acc, s) => acc + s.duration_seconds, 0) / data.session_history.length)}s
                </div>
                <div className="text-gray-500">Avg Session Time</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Speaking Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Recent Speaking Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.speaking_sessions || data.speaking_sessions.length === 0) ? (
              <p className="text-gray-500 text-sm">No speaking sessions yet</p>
            ) : (
              <div className="space-y-3">
                {data.speaking_sessions.slice(0, 2).map((session, idx) => {
                  const key = (session && (session.session_id || (session as any).sessionId)) || idx;
                  const level = (session as any)?.speaking_level ?? (session as any)?.speakingLevel ?? '—';
                  const durationSeconds = (session as any)?.duration_seconds ?? (session as any)?.durationSeconds ?? 0;
                  const scores = (session as any)?.scores ?? { pronunciation: 0, fluency: 0, grammar: 0, overall: 0 };

                  return (
                    <div key={key} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 last:pb-0">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-app-text dark:text-white">
                            Session
                          </span>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                            {level}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDuration(durationSeconds / 60)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        <span className="text-gray-500">Topic: </span>
                        <span className="font-medium text-app-text dark:text-gray-200">
                          {resolveSpeakingTopicLabel(session as SpeakingMetricsDoc, data.session_history) || '—'}
                        </span>
                      </p>
                      <div className="space-y-3">
                        {[
                          { label: 'Pronunciation', value: scores.pronunciation ?? 0, color: 'bg-emerald-500' },
                          { label: 'Fluency', value: scores.fluency ?? 0, color: 'bg-sky-500' },
                          { label: 'Grammar', value: scores.grammar ?? 0, color: 'bg-violet-500' },
                          { label: 'Overall', value: scores.overall ?? 0, color: 'bg-indigo-500', bold: true },
                        ].map(({ label, value, color, bold }) => {
                          const safe = Math.min(100, Math.max(0, Number(value) || 0));
                          return (
                            <div key={label} className="flex items-center gap-2">
                              <span className={`text-xs text-gray-500 dark:text-gray-400 w-24 shrink-0 ${bold ? 'font-semibold text-gray-700 dark:text-gray-300' : ''}`}>
                                {label}:
                              </span>
                              <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${color} transition-all duration-500`}
                                  style={{ width: `${safe}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium w-8 text-right ${bold ? 'font-bold text-app-text dark:text-white' : ''}`}>
                                {safe}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Engagement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.daily_engagement || data.daily_engagement.length === 0) ? (
              <p className="text-gray-500 text-sm">No engagement data yet</p>
            ) : (
              <div className="space-y-3">
                {data.daily_engagement.slice(0, 5).map((day) => (
                  <div key={day.date} className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {new Date(day.date).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {day.sessions} sessions
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {formatDuration(day.tutor_minutes)}
                      </div>
                      <div className="text-xs text-gray-500">
                        minutes
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ParentTutorAnalyticsPanel;

